/**
 * ADP paydata serializer — Time Evaluation Phase 4b.
 *
 * Turns `EvaluatedTimeRecord[]` (the premium-aware output of
 * `lib/timeEvaluationEngine.ts`) into the CSV grid an ADP Workforce Now paydata
 * import expects, driven entirely by an `AdpMappingProfile` so the column set,
 * the header TEXT and the earnings codes are the client's, not ours.
 *
 * ── The CSV import trap (read this before touching an import line) ────────────
 * This file uses `serializeCsv` from `@/components/reports/lib/csvExport`.
 * It does NOT use `modules/timesheet/lib/export.ts`, which exports a RIVAL
 * `downloadCsv` whose arguments are REVERSED (`downloadCsv(rows, filename)` vs
 * `downloadCsv(filename, rows)`). Both are one auto-import keystroke away from
 * each other and both typecheck against a loose call site. Importing the wrong
 * one produces a file named after its own contents. Keep this import exact.
 *
 * ── What the file actually contains ───────────────────────────────────────────
 * ADP paydata is PERIOD level: one row per (Co Code x File # x Batch). So the
 * default here aggregates every evaluated record for a worker across the whole
 * pay period, per earnings code. `profile.rowGranularity: 'day'` opts into one
 * row per work date, for the clients whose WFN grid carries a work-date column.
 *
 * Slot allocation:
 *   • `profile.regularCodes`  -> the `Reg Hours` column (a fixed `earningsHours` binding)
 *   • `profile.overtimeCodes` -> the `O/T Hours` column
 *   • every OTHER hours-denominated code -> `Hours 3/4 Code` + `Amount`, sorted
 *     by hours DESCENDING (so the biggest premium is never the one that spills)
 *   • every DOLLAR-denominated line (a flat premium: lead, hazard, jury,
 *     bereavement, NY spread-of-hours) -> `Earnings 3/4 Code` + `Amount`
 *
 * A record is DOLLAR-denominated when it carries zero hours. That is the honest
 * discriminator: the engine emits flats as `hours: 0` with the dollars in
 * `amountDollars`, and putting 0.00 in an Hours slot pays nothing while putting
 * 8 hours in an Earnings slot pays $8.00.
 *
 * ── Overflow is NOT an error ──────────────────────────────────────────────────
 * When a worker has more premium codes than the profile has slots and
 * `slotOverflow` is `'spill_extra_row'`, this emits a SECOND row for the same
 * File # in the same batch. **WFN accepts multiple rows per associate**, so that
 * is correct behavior, not a failure. The spill rows leave `Reg Hours` /
 * `O/T Hours` (and every other fixed `earningsHours`/`earningsAmount` column)
 * BLANK, because repeating them would pay those hours twice. Every spill is
 * reported in `overflow` so the UI can say what happened. `'block'` exists for
 * the clients whose ADP config forbids multiple rows per associate.
 *
 * ── Format facts ADP's importer cares about ───────────────────────────────────
 * CRLF by default (its importer is Windows-oriented; `profile.lineEnding` can
 * force LF), UTF-8 with **no BOM** (a BOM lands inside the first header cell and
 * WFN then cannot find the `Co Code` column), no thousands separators, hours
 * 2dp, money 2dp, dates MM/DD/YYYY. All of that is enforced by `formatAdpCell`
 * in `lib/adpMappingProfile.ts`, which owns the formatting policy.
 *
 * PURE. No I/O, no DOM, no Supabase. The download leg lives in Phase 5.
 */
// ⚠️ EXACT IMPORT — see "The CSV import trap" above. NOT '../lib/export'.
import { serializeCsv, type CsvCell } from '@/components/reports/lib/csvExport'
import type { PayrollWorker } from '@/integrations/adapterContracts'
import {
  ADP_FIRST_SLOT_ORDINAL,
  ADP_LINE_ENDING,
  formatAdpCell,
  mapEarningsCodeWithFallback,
  renderBatchId,
  resolveCoCode,
  type AdpColumnMapping,
  type AdpColumnSource,
  type AdpMappingProfile,
} from '../lib/adpMappingProfile'
import type { EvaluatedTimeRecord } from '../types/timeEvaluation.types'
import type { PayrollPeriod } from './buildPayrollBatch'

// ─── Blockers + overflow ───────────────────────────────────────────────────────

/**
 * Why a worker with evaluated hours cannot be written into the ADP file.
 *
 * The first two share `PayrollBuildBlocker`'s vocabulary verbatim (see
 * `buildPayrollBatch.ts`) so the exceptions report reads the same whichever
 * stage produced it. The last three are ADP-specific and have no equivalent in
 * the vendor-neutral stage.
 */
export type AdpExportBlockerReason =
  /** No payroll crosswalk row at all — the worker has no ADP File #. */
  | 'no_worker_record'
  /** Crosswalk row exists but the File # is blank. */
  | 'no_payroll_id'
  /** The profile has no Co Code for this payroll tenant. */
  | 'no_co_code'
  /** An earnings code needing a Code cell is not in `earningsCodeMap` (nor `hcm_code`). */
  | 'unmapped_earnings_code'
  /** More premium codes than slots, and the profile forbids spilling. */
  | 'slot_overflow'
  /** EPIP, amount mode "evaluated": hours with no evaluated dollars (no base rate). */
  | 'unpriced_hours'
  /** EPIP fixed width: a File # or earning code longer than its field. */
  | 'field_too_wide'
  /** EPIP hours-only file: the worker has a dollar-only earning it cannot carry. */
  | 'dollar_earning_not_allowed'

/**
 * One worker who is NOT in the file. A blocked worker contributes ZERO rows —
 * never a partial row, because a partial row underpays silently and looks
 * successful. Same all-or-nothing rule `buildPayrollBatch` applies.
 */
export interface AdpExportBlocker {
  employeeId: string
  workerName: string
  reason: AdpExportBlockerReason
  /** Total evaluated hours in the period for this worker — what is at stake. */
  hours: number
  /** The internal earnings code, when the blocker is about one specific code. */
  earningsCodeInternal?: string
  /** One sentence a payroll admin can act on. */
  detail: string
}

/**
 * A worker whose premium codes outran the profile's slots.
 *
 * With `slotOverflow: 'spill_extra_row'` this is INFORMATIONAL — the extra rows
 * were written and the file is complete. It is surfaced so the UI can say "3
 * associates needed a second row", which is also the signal that the client
 * should ask ADP to widen their paydata grid.
 */
export interface AdpSlotOverflow {
  employeeId: string
  workerName: string
  /** The ADP File # the extra rows were written under. */
  fileNumber: string
  /** The ADP Co Code, or '' when the profile emits no Co Code column. */
  coCode: string
  /** Present only at `rowGranularity: 'day'`. */
  date?: string
  family: 'hours' | 'earnings'
  /** Slots the profile declares for this family. */
  slotCount: number
  /** Internal codes that did not fit on the FIRST row, in emission order. */
  spilledCodes: string[]
  /** The ADP codes for `spilledCodes`, same order. */
  spilledAdpCodes: string[]
  /** Extra rows emitted for this worker/date. 0 when `resolution` is 'block'. */
  extraRows: number
  resolution: 'spill_extra_row' | 'block'
}

// ─── Inputs ────────────────────────────────────────────────────────────────────

export interface AdpPaydataArgs {
  /** Evaluated records for ONE payroll tenant (partition first — see below). */
  records: readonly EvaluatedTimeRecord[]
  profile: AdpMappingProfile
  period: PayrollPeriod
  /**
   * The payroll tenant this file is for. `null` = untagged / single-tenant,
   * which looks up `ADP_DEFAULT_TENANT_KEY` ('') in `companyCodeByTenant`.
   *
   * A File # is only unique WITHIN a Co Code, so a multi-tenant customer runs
   * this once per tenant off `partitionPayrollBatchByTenant`. Passing mixed
   * tenants produces one file in one company — which is how a real payroll ends
   * up in the wrong entity.
   */
  tenant: string | null
  /**
   * employeeId -> payroll crosswalk. `payrollId` IS the ADP File #. A worker
   * missing here (or with a blank id) is a blocker, never a blank cell.
   * Populate from `services/adpWorkerCrosswalkService.listAdpWorkerCrosswalk`.
   */
  workers?: Record<string, PayrollWorker | undefined>
  /** Optional `{SEQ}` value for the batch-id template. */
  sequence?: number | string | null
}

export interface AdpPaydataResult {
  /** Header row (when `includeHeaderRow`) followed by the data rows. */
  rows: CsvCell[][]
  overflow: AdpSlotOverflow[]
  blockers: AdpExportBlocker[]
}

// ─── Small helpers ─────────────────────────────────────────────────────────────

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** The `''` bucket key used at period granularity — one bucket for the period. */
const PERIOD_BUCKET_KEY = ''

function upper(s: string | null | undefined): string {
  return (s ?? '').trim().toUpperCase()
}

function chunk<T>(list: T[], size: number): T[][] {
  if (size <= 0) return list.length === 0 ? [] : [list]
  const out: T[][] = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/** Slot ordinal -> zero-based index into a chunk. Ordinals start at 3, not 0. */
function slotIndex(ordinal: number): number {
  return ordinal - ADP_FIRST_SLOT_ORDINAL
}

/** Running totals for ONE internal earnings code within one bucket. */
interface CodeTotals {
  /** `EvaluatedTimeRecord.earningsCodeInternal`. */
  internal: string
  /** The client's ADP code, or null when the profile does not map it. */
  adp: string | null
  /** Sum of `hours` across the bucket's records for this code. */
  hours: number
  /** Sum of `amountDollars` across records that carry ZERO hours. */
  dollars: number
}

interface WorkerBucket {
  /** '' at period granularity; the ISO work date at day granularity. */
  key: string
  codes: Map<string, CodeTotals>
}

interface WorkerGroup {
  employeeId: string
  buckets: Map<string, WorkerBucket>
  /** Every hour the engine evaluated for this worker in the period. */
  totalHours: number
}

// ─── Column classification ─────────────────────────────────────────────────────

interface ProfileShape {
  /** Internal codes whose HOURS a fixed `earningsHours` column already carries. */
  fixedHoursCodes: Set<string>
  /** Internal codes whose DOLLARS a fixed `earningsAmount` column already carries. */
  fixedDollarCodes: Set<string>
  /** Does any column emit the Co Code? (The generic preset does not.) */
  bindsCoCode: boolean
  hoursSlots: number
  earningsSlots: number
}

/**
 * What the profile's column list actually binds.
 *
 * The fixed-code sets are the reason a premium never double-pays: a code a
 * dedicated column already carries is REMOVED from the dynamic slot pool.
 * `validateAdpProfile` reports the config-time version of the same hazard.
 *
 * `regularCodes` / `overtimeCodes` are folded in even when no column names them,
 * so a profile that declares them but forgot the column still does not spill
 * REG into `Hours 3` (that would pay regular time twice — once as base, once as
 * a premium code).
 */
function describeProfile(profile: AdpMappingProfile): ProfileShape {
  const fixedHoursCodes = new Set<string>()
  const fixedDollarCodes = new Set<string>()
  let bindsCoCode = false
  for (const c of profile.columns ?? []) {
    const s = c.source
    if (s.kind === 'field' && s.field === 'coCode') bindsCoCode = true
    if (s.kind === 'earningsHours') for (const code of s.earningsCodes ?? []) fixedHoursCodes.add(upper(code))
    if (s.kind === 'earningsAmount') for (const code of s.earningsCodes ?? []) fixedDollarCodes.add(upper(code))
  }
  for (const code of profile.regularCodes ?? []) fixedHoursCodes.add(upper(code))
  for (const code of profile.overtimeCodes ?? []) fixedHoursCodes.add(upper(code))
  return {
    fixedHoursCodes,
    fixedDollarCodes,
    bindsCoCode,
    hoursSlots: Math.max(0, Math.floor(profile.hoursSlots ?? 0)),
    earningsSlots: Math.max(0, Math.floor(profile.earningsSlots ?? 0)),
  }
}

// ─── Aggregation ───────────────────────────────────────────────────────────────

/**
 * Roll records into worker -> bucket -> internal code.
 *
 * Records outside `[period.start, period.end]` are dropped, matching
 * `buildPayrollBatch`'s defensive window cut: the caller usually already
 * filtered, and a stray record silently paid in the wrong period is worse than
 * a stray record dropped from a period it does not belong to.
 */
function groupRecords(
  records: readonly EvaluatedTimeRecord[],
  profile: AdpMappingProfile,
): Map<string, WorkerGroup> {
  const byWorker = new Map<string, WorkerGroup>()
  const perDay = profile.rowGranularity === 'day'

  for (const r of records) {
    const group = byWorker.get(r.employeeId) ?? {
      employeeId: r.employeeId,
      buckets: new Map<string, WorkerBucket>(),
      totalHours: 0,
    }
    const key = perDay ? r.date : PERIOD_BUCKET_KEY
    const bucket = group.buckets.get(key) ?? { key, codes: new Map<string, CodeTotals>() }
    const internal = (r.earningsCodeInternal ?? '').trim()
    const codeKey = upper(internal)
    const totals = bucket.codes.get(codeKey) ?? {
      internal,
      adp: mapEarningsCodeWithFallback(profile, internal, r.hcmCode),
      hours: 0,
      dollars: 0,
    }
    // hours-denominated vs dollar-denominated: the engine emits flats as
    // `hours: 0` with the money in `amountDollars`.
    if (r.hours > 0) totals.hours += r.hours
    else totals.dollars += r.amountDollars
    // A later record can resolve an ADP code an earlier one could not (a
    // catalog `hcm_code` on only one of the lines). Keep the first non-null.
    if (!totals.adp) totals.adp = mapEarningsCodeWithFallback(profile, internal, r.hcmCode)
    bucket.codes.set(codeKey, totals)
    group.buckets.set(key, bucket)
    group.totalHours += r.hours
    byWorker.set(r.employeeId, group)
  }

  for (const group of byWorker.values()) {
    group.totalHours = round2(group.totalHours)
    for (const bucket of group.buckets.values()) {
      for (const t of bucket.codes.values()) {
        t.hours = round2(t.hours)
        t.dollars = round2(t.dollars)
      }
    }
  }
  return byWorker
}

/**
 * Split one bucket's codes into the four destinations.
 *
 * Ordering is `hours`/`dollars` DESCENDING then internal code ASCENDING. The
 * descending part means the biggest premium is never the one that spills to a
 * second row; the code tiebreak means two runs of the same period produce a
 * byte-identical file, which is the only way a payroll diff is meaningful.
 */
function classifyBucket(bucket: WorkerBucket, shape: ProfileShape) {
  const slotHours: CodeTotals[] = []
  const slotDollars: CodeTotals[] = []
  for (const t of bucket.codes.values()) {
    const key = upper(t.internal)
    if (t.hours > 0) {
      if (!shape.fixedHoursCodes.has(key)) slotHours.push(t)
    } else if (t.dollars !== 0) {
      if (!shape.fixedDollarCodes.has(key)) slotDollars.push(t)
    }
  }
  slotHours.sort((a, b) => b.hours - a.hours || a.internal.localeCompare(b.internal))
  slotDollars.sort(
    (a, b) => Math.abs(b.dollars) - Math.abs(a.dollars) || a.internal.localeCompare(b.internal),
  )
  return { slotHours, slotDollars }
}

// ─── Row rendering ─────────────────────────────────────────────────────────────

interface RowContext {
  profile: AdpMappingProfile
  coCode: string
  batchId: string
  fileNumber: string
  employeeName: string
  tenant: string | null
  periodEnd: string
  /** '' at period granularity. */
  workDate: string
  bucket: WorkerBucket
  hoursChunk: CodeTotals[]
  earnChunk: CodeTotals[]
  /**
   * TRUE only on the first row for this worker/date. Fixed `earningsHours` /
   * `earningsAmount` columns emit ONLY on the first row; a spill row that
   * repeated `Reg Hours` would pay those hours a second time.
   */
  isFirstRow: boolean
}

function sumFixed(bucket: WorkerBucket, codes: string[], field: 'hours' | 'dollars'): number | null {
  let total = 0
  let hit = false
  for (const code of codes ?? []) {
    const t = bucket.codes.get(upper(code))
    if (!t) continue
    hit = true
    total += t[field]
  }
  return hit ? round2(total) : null
}

function cellValue(source: AdpColumnSource, ctx: RowContext): string | number | null {
  switch (source.kind) {
    case 'literal':
      return source.value
    case 'blank':
      return null
    case 'field':
      switch (source.field) {
        case 'coCode':
          return ctx.coCode || null
        case 'batchId':
          return ctx.batchId || null
        case 'fileNumber':
          return ctx.fileNumber || null
        case 'workDate':
          return ctx.workDate || null
        case 'periodEnd':
          return ctx.periodEnd || null
        case 'employeeName':
          return ctx.employeeName || null
        case 'payrollTenant':
          return ctx.tenant || null
        // No evaluated record carries a department override, a temporary rate
        // or a position id today. Emitted blank on purpose: a Temp Rate we
        // invented would silently override the associate's real ADP rate for
        // every hour on the row.
        case 'tempDept':
        case 'tempRate':
        case 'positionId':
          return null
        default:
          return null
      }
    case 'earningsHours':
      return ctx.isFirstRow ? sumFixed(ctx.bucket, source.earningsCodes ?? [], 'hours') : null
    case 'earningsAmount':
      return ctx.isFirstRow ? sumFixed(ctx.bucket, source.earningsCodes ?? [], 'dollars') : null
    case 'hoursSlotCode':
      return ctx.hoursChunk[slotIndex(source.slot)]?.adp ?? null
    case 'hoursSlotAmount': {
      const t = ctx.hoursChunk[slotIndex(source.slot)]
      return t ? t.hours : null
    }
    case 'earningsSlotCode':
      return ctx.earnChunk[slotIndex(source.slot)]?.adp ?? null
    case 'earningsSlotAmount': {
      const t = ctx.earnChunk[slotIndex(source.slot)]
      return t ? t.dollars : null
    }
    default:
      return null
  }
}

function renderRow(columns: AdpColumnMapping[], ctx: RowContext): CsvCell[] {
  return columns.map((c) => formatAdpCell(cellValue(c.source, ctx), c.format, ctx.profile.emptyValue))
}

// ─── The build ─────────────────────────────────────────────────────────────────

/**
 * Build the ADP paydata grid for one payroll tenant.
 *
 * Returns the rows (header first, when the profile asks for one), every slot
 * overflow, and every blocked worker. A blocked worker contributes NO rows.
 * Deterministic: workers are emitted in File # order, dates ascending, codes in
 * the stable order `classifyBucket` defines.
 */
export function buildAdpPaydataRows(args: AdpPaydataArgs): AdpPaydataResult {
  const { profile, period, tenant } = args
  const workers = args.workers ?? {}
  const columns = profile.columns ?? []
  const shape = describeProfile(profile)

  const inPeriod = (args.records ?? []).filter(
    (r) => r.date >= period.start && r.date <= period.end,
  )
  const groups = groupRecords(inPeriod, profile)

  const coCode = resolveCoCode(profile, tenant)
  const batchId = renderBatchId(profile.batchIdTemplate ?? '', period, {
    coCode,
    tenant,
    sequence: args.sequence ?? null,
  })

  const blockers: AdpExportBlocker[] = []
  const overflow: AdpSlotOverflow[] = []
  const emitted: { fileNumber: string; rows: CsvCell[][] }[] = []

  for (const group of [...groups.values()].sort((a, b) => a.employeeId.localeCompare(b.employeeId))) {
    const worker = workers[group.employeeId]
    const workerName = worker?.name?.trim() || group.employeeId
    const hours = group.totalHours

    if (!worker) {
      blockers.push({
        employeeId: group.employeeId,
        workerName,
        reason: 'no_worker_record',
        hours,
        detail:
          `No ADP crosswalk row for this worker. Import their File # (and Co Code) ` +
          `before running the file.`,
      })
      continue
    }
    const fileNumber = (worker.payrollId ?? '').trim()
    if (!fileNumber) {
      blockers.push({
        employeeId: group.employeeId,
        workerName,
        reason: 'no_payroll_id',
        hours,
        detail: 'The ADP crosswalk row exists but carries no File #.',
      })
      continue
    }
    if (shape.bindsCoCode && !coCode) {
      blockers.push({
        employeeId: group.employeeId,
        workerName,
        reason: 'no_co_code',
        hours,
        detail:
          `The profile has no ADP Co Code for payroll tenant ` +
          `"${tenant ?? '(untagged)'}". A File # is only unique within a Co Code, ` +
          `so the row cannot be routed.`,
      })
      continue
    }

    // ── Per-bucket planning. Every problem is collected BEFORE any row is
    //    emitted, so a worker is either wholly in the file or wholly blocked.
    const bucketKeys = [...group.buckets.keys()].sort()
    const plans: {
      bucket: WorkerBucket
      hoursChunks: CodeTotals[][]
      earnChunks: CodeTotals[][]
      rowCount: number
    }[] = []
    let workerBlocked = false

    for (const key of bucketKeys) {
      const bucket = group.buckets.get(key)!
      const { slotHours, slotDollars } = classifyBucket(bucket, shape)

      // An unmapped code that needs a Code cell must NEVER be written: ADP
      // silently drops an unknown earnings code, so the worker just quietly
      // does not get paid the premium. A code carried by a FIXED column
      // (Reg Hours / O/T Hours) needs no map entry — the column IS the binding.
      for (const t of [...slotHours, ...slotDollars]) {
        if (!t.adp) {
          workerBlocked = true
          blockers.push({
            employeeId: group.employeeId,
            workerName,
            reason: 'unmapped_earnings_code',
            hours,
            earningsCodeInternal: t.internal,
            detail:
              `Internal earnings code "${t.internal}" has no ADP code in the profile's ` +
              `earningsCodeMap and no hcm_code on the catalog row. ADP drops unknown ` +
              `codes silently, so the whole worker is held back.`,
          })
        }
      }

      const noHoursRoom = shape.hoursSlots <= 0 && slotHours.length > 0
      const noEarnRoom = shape.earningsSlots <= 0 && slotDollars.length > 0
      if (noHoursRoom || noEarnRoom) {
        workerBlocked = true
        blockers.push({
          employeeId: group.employeeId,
          workerName,
          reason: 'slot_overflow',
          hours,
          detail:
            `The profile declares ${shape.hoursSlots} hours slot(s) and ` +
            `${shape.earningsSlots} earnings slot(s), which leaves no room for ` +
            `${noHoursRoom ? slotHours.length : slotDollars.length} premium code(s). ` +
            `Add slots to the profile (ask ADP to widen the paydata grid).`,
        })
        continue
      }

      const hoursChunks = chunk(slotHours, shape.hoursSlots)
      const earnChunks = chunk(slotDollars, shape.earningsSlots)
      const rowCount = Math.max(1, hoursChunks.length, earnChunks.length)

      if (rowCount > 1) {
        const spillHours = slotHours.slice(shape.hoursSlots)
        const spillEarn = slotDollars.slice(shape.earningsSlots)
        const block = profile.slotOverflow === 'block'
        if (block) workerBlocked = true
        for (const fam of [
          { family: 'hours' as const, spilled: spillHours, slotCount: shape.hoursSlots },
          { family: 'earnings' as const, spilled: spillEarn, slotCount: shape.earningsSlots },
        ]) {
          if (fam.spilled.length === 0) continue
          overflow.push({
            employeeId: group.employeeId,
            workerName,
            fileNumber,
            coCode: coCode ?? '',
            ...(key ? { date: key } : {}),
            family: fam.family,
            slotCount: fam.slotCount,
            spilledCodes: fam.spilled.map((t) => t.internal),
            spilledAdpCodes: fam.spilled.map((t) => t.adp ?? ''),
            extraRows: block ? 0 : rowCount - 1,
            resolution: block ? 'block' : 'spill_extra_row',
          })
        }
        if (block) {
          blockers.push({
            employeeId: group.employeeId,
            workerName,
            reason: 'slot_overflow',
            hours,
            detail:
              `${spillHours.length + spillEarn.length} earnings code(s) exceed the ` +
              `profile's slots and slotOverflow is "block", so no row was written. ` +
              `Switch the profile to "spill_extra_row" (WFN does accept multiple rows ` +
              `per associate) or add slots.`,
          })
          continue
        }
      }

      plans.push({ bucket, hoursChunks, earnChunks, rowCount })
    }

    if (workerBlocked) continue

    const rows: CsvCell[][] = []
    for (const plan of plans) {
      for (let i = 0; i < plan.rowCount; i++) {
        rows.push(
          renderRow(columns, {
            profile,
            coCode: coCode ?? '',
            batchId,
            fileNumber,
            employeeName: worker.name ?? '',
            tenant,
            periodEnd: period.end,
            workDate: plan.bucket.key,
            bucket: plan.bucket,
            hoursChunk: plan.hoursChunks[i] ?? [],
            earnChunk: plan.earnChunks[i] ?? [],
            isFirstRow: i === 0,
          }),
        )
      }
    }
    if (rows.length > 0) emitted.push({ fileNumber, rows })
  }

  // File # order — that is the order a payroll admin scans the file in, and
  // ADP's own grid is sorted that way.
  emitted.sort((a, b) => a.fileNumber.localeCompare(b.fileNumber))

  const rows: CsvCell[][] = []
  if (profile.includeHeaderRow) rows.push(columns.map((c) => c.column))
  for (const e of emitted) rows.push(...e.rows)

  blockers.sort(
    (a, b) =>
      a.employeeId.localeCompare(b.employeeId) ||
      a.reason.localeCompare(b.reason) ||
      (a.earningsCodeInternal ?? '').localeCompare(b.earningsCodeInternal ?? ''),
  )

  return { rows, overflow, blockers }
}

// ─── Serialization ─────────────────────────────────────────────────────────────

/**
 * The finished CSV text.
 *
 * CRLF unless the profile says LF, and **no BOM** — a BOM ends up inside the
 * first header cell and WFN then cannot find the `Co Code` column. Quoting +
 * escaping come from the shared `serializeCsv`; it is called one row at a time
 * so the profile's line ending wins over its hardcoded '\n'.
 *
 * No trailing newline: ADP's importer reads a trailing blank line as an empty
 * record on some grids.
 */
export function serializeAdpPaydataCsv(args: AdpPaydataArgs): string {
  return serializeAdpPaydataRowsCsv(buildAdpPaydataRows(args).rows, args.profile)
}

/** Same serialization, for rows you already built (avoids a second build pass). */
export function serializeAdpPaydataRowsCsv(
  rows: CsvCell[][],
  profile: Pick<AdpMappingProfile, 'lineEnding'>,
): string {
  const eol = ADP_LINE_ENDING[profile.lineEnding] ?? ADP_LINE_ENDING.crlf
  return rows.map((row) => serializeCsv([row])).join(eol)
}

/**
 * Stable, filesystem-safe download name.
 *
 * Shape: `adp-paydata_<profile>_<period label>[_<tenant>]_<start>_<end>.csv`.
 * The profile id is in there because a customer running two ADP companies has
 * two profiles, and two files called `adp-paydata_PP-01.csv` in a Downloads
 * folder is how the wrong one gets uploaded.
 */
export function adpPaydataFilename(
  profile: Pick<AdpMappingProfile, 'id'>,
  period: PayrollPeriod,
  tenant: string | null,
): string {
  const safe = (s: string) => (s ?? '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '')
  const tenantSeg = tenant?.trim() ? `_${safe(tenant)}` : ''
  return `adp-paydata_${safe(profile.id)}_${safe(period.label)}${tenantSeg}_${period.start}_${period.end}.csv`
}

/** Everything Phase 5's download button needs, in one call. */
export interface AdpPaydataFile extends AdpPaydataResult {
  filename: string
  csv: string
}

export function buildAdpPaydataFile(args: AdpPaydataArgs): AdpPaydataFile {
  const built = buildAdpPaydataRows(args)
  return {
    ...built,
    filename: adpPaydataFilename(args.profile, args.period, args.tenant),
    csv: serializeAdpPaydataRowsCsv(built.rows, args.profile),
  }
}

// ─── Read-back summary ─────────────────────────────────────────────────────────

export interface AdpRowSummary {
  /** Every hours-denominated cell in the file, summed. */
  totalHours: number
  /** ADP earnings code -> hours. */
  hoursByAdpCode: Record<string, number>
  /** ADP earnings code -> dollars. */
  dollarsByAdpCode: Record<string, number>
}

/**
 * Re-read emitted rows THROUGH the profile and total them by ADP code.
 *
 * This is deliberately an independent read of the OUTPUT rather than a second
 * look at the input records — it is what lets `evaluatedToPayrollBatch.test.ts`
 * prove the QA preview and the ADP file carry the same hours, and what a Phase 5
 * "what is in this file" panel should display.
 *
 * Fixed `earningsHours` / `earningsAmount` columns are keyed on the ADP code of
 * their FIRST internal code (`Reg Hours` -> whatever `REG` maps to). Slot columns
 * are keyed on the Code cell, which already holds the ADP code.
 */
export function summarizeAdpRows(profile: AdpMappingProfile, rows: CsvCell[][]): AdpRowSummary {
  const columns = profile.columns ?? []
  const dataRows = profile.includeHeaderRow ? rows.slice(1) : rows
  const hoursByAdpCode: Record<string, number> = {}
  const dollarsByAdpCode: Record<string, number> = {}
  let totalHours = 0

  const num = (cell: CsvCell): number => {
    if (cell == null) return 0
    const n = Number(String(cell).trim())
    return Number.isFinite(n) ? n : 0
  }
  const codeCell = (row: CsvCell[], family: 'hours' | 'earnings', ordinal: number): string => {
    const idx = columns.findIndex(
      (c) =>
        c.source.kind === (family === 'hours' ? 'hoursSlotCode' : 'earningsSlotCode') &&
        (c.source as { slot: number }).slot === ordinal,
    )
    return idx >= 0 ? String(row[idx] ?? '').trim() : ''
  }

  for (const row of dataRows) {
    columns.forEach((c, i) => {
      const s = c.source
      if (s.kind === 'earningsHours') {
        const first = (s.earningsCodes ?? [])[0] ?? ''
        const key = mapEarningsCodeWithFallback(profile, first) ?? first
        const v = num(row[i])
        if (v === 0) return
        hoursByAdpCode[key] = round2((hoursByAdpCode[key] ?? 0) + v)
        totalHours = round2(totalHours + v)
      } else if (s.kind === 'earningsAmount') {
        const first = (s.earningsCodes ?? [])[0] ?? ''
        const key = mapEarningsCodeWithFallback(profile, first) ?? first
        const v = num(row[i])
        if (v === 0) return
        dollarsByAdpCode[key] = round2((dollarsByAdpCode[key] ?? 0) + v)
      } else if (s.kind === 'hoursSlotAmount') {
        const key = codeCell(row, 'hours', s.slot)
        const v = num(row[i])
        if (!key || v === 0) return
        hoursByAdpCode[key] = round2((hoursByAdpCode[key] ?? 0) + v)
        totalHours = round2(totalHours + v)
      } else if (s.kind === 'earningsSlotAmount') {
        const key = codeCell(row, 'earnings', s.slot)
        const v = num(row[i])
        if (!key || v === 0) return
        dollarsByAdpCode[key] = round2((dollarsByAdpCode[key] ?? 0) + v)
      }
    })
  }

  return { totalHours: round2(totalHours), hoursByAdpCode, dollarsByAdpCode }
}
